import React, { useRef, useState } from 'react'
import { Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import * as yup from 'yup'
import { useEffectOnce } from 'react-use'
import { isEmpty, mergeRight, path } from 'ramda'

import { FormErrors, FormField, FormLoader, TextField, TextInput } from '../../../../../views/components'
import { userApiEvents } from '../../../../api/user'
import { apiStateSelectors } from '../../../../api/apiState'
import styles from './InvitationForm.module.scss'
import { formUtils } from '../../../../../utils'
import { $TSFixMe } from 'types/ts-migrate'
import { TextFieldTypes } from 'components/atoms/TextField/text-field.types'
import { getMatchedPasswordCriteria, passwordConstraintContent } from 'views/components/organisms/Forms/passwordUtils'
import SvgEyeFill from 'views/components/atoms/Icons/Controls/Eye'
import SvgEyeSlashFill from 'views/components/atoms/Icons/Controls/EyeSlash'

interface InvitationFormProps {
  agencyName: string
  firstName: string
  lastName: string
  token: string
}

const validationSchema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required.'),
  password: yup
    .string()
    .required('Password is required.')
    .min(12, 'Password must be at least 12 characters.')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
})

const ButtonContainer = ({ children }: any): React.ReactElement => (
  <div className={styles['submit-button']}>{children}</div>
)

const InvitationForm = ({ agencyName, firstName, lastName, token }: InvitationFormProps): React.ReactElement => {
  const dispatch = useDispatch()
  const passwordRef = useRef<any>()

  const [showPassword, setShowPassword] = useState(false)

  const isSubmitting = useSelector(state =>
    apiStateSelectors.isLoading(state)([userApiEvents.signup_request, userApiEvents.signupFromInvite_request]),
  )
  const getGeneralFormErrorsByEvent = useSelector(apiStateSelectors.getGeneralFormErrorsByEvent)
  const getFormFieldErrorsByEvent = useSelector(apiStateSelectors.getFormFieldErrorsByEvent)

  const togglePasswordVisibility = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setShowPassword(!showPassword)
  }

  useEffectOnce(() => {
    passwordRef.current?.focus()
  })

  return (
    <div className={styles.root}>
      <h2>Accept invitation</h2>
      <p>You were invited to join {agencyName}.</p>
      <p>
        Please confirm your name and enter a password below to get started.
        <br />
        <a
          href="https://help.reos.co.za/en/articles/13884289-creating-a-strong-password"
          target="_blank"
          rel="noopener noreferrer"
        >
          Tips for creating a strong password
        </a>
      </p>
      <Formik
        initialValues={{ firstName, lastName, password: '' }}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={({ firstName, lastName, password }) => {
          dispatch(userApiEvents.signupFromInvite_request({ firstName, lastName, password, encryptedValue: token }))
        }}
      >
        {({ handleSubmit, handleBlur, handleChange, errors, values, touched, submitCount }) => {
          const registerEvent = userApiEvents.signupFromInvite_request({ ...values, encryptedValue: token })
          const fieldErrors = getFormFieldErrorsByEvent(registerEvent)
          const generalErrors = getGeneralFormErrorsByEvent(registerEvent) || []
          const emailError: $TSFixMe = path(['email'], fieldErrors)
          /**
           * @todo temp hack because there is no 'email' field in this form.
                  The API returns email exists error under errors.email instead of errors.general
           */
          if (emailError) {
            generalErrors.push(`${emailError}`)
          }
          const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)

          const formLoaderState = isSubmitting
            ? 'submitting'
            : (submitCount > 0 && !isEmpty(errors)) || hasApiErrors
            ? 'error'
            : undefined

          const getFieldError = formUtils.getFieldError(submitCount, touched, mergeRight(errors, fieldErrors))

          const matchedCriteria = getMatchedPasswordCriteria(values.password)

          return (
            <>
              <FormErrors errors={generalErrors} />
              <FormLoader
                onSubmit={handleSubmit}
                ButtonContainer={ButtonContainer}
                buttonProps={{ children: 'Submit' }}
                state={formLoaderState}
                errorText={hasApiErrors ? 'Oops, something went wrong' : 'Resolve errors'}
                persistErrorMessage={!hasApiErrors}
              >
                <FormField>
                  <TextField
                    inputComponent={
                      <TextInput
                        type={TextFieldTypes.text}
                        name="firstName"
                        placeholder="First name"
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    }
                    error={getFieldError('firstName')}
                  />
                </FormField>
                <FormField>
                  <TextField
                    inputComponent={
                      <TextInput
                        type={TextFieldTypes.text}
                        name="lastName"
                        placeholder="Last name"
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    }
                    error={getFieldError('lastName')}
                  />
                </FormField>
                <FormField>
                  <TextField
                    inputComponent={
                      <div style={{ position: 'relative' }} className={styles['password-input']}>
                        <TextInput
                          type={showPassword ? TextFieldTypes.text : TextFieldTypes.password}
                          id="password"
                          name="password"
                          placeholder="Choose a password"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          ref={(ref: any) => {
                            passwordRef.current = ref
                          }}
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className={styles['password-toggle']}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          aria-pressed={showPassword}
                        >
                          {showPassword ? <SvgEyeFill /> : <SvgEyeSlashFill />}
                        </button>
                      </div>
                    }
                    error={getFieldError('password')}
                  />
                </FormField>
                <div className={styles['password-criteria']}>
                  {passwordConstraintContent?.map((child, index) => (
                    <div
                      key={child.id}
                      className={matchedCriteria?.includes(child.name) ? styles.matched : styles.default}
                    >
                      <p>{child.message}</p>
                    </div>
                  ))}
                </div>
              </FormLoader>
            </>
          )
        }}
      </Formik>
    </div>
  )
}

export default InvitationForm
